import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { API_BASE, setToken, getToken } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (!getToken()) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch {
      setToken(null);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password, totpCode = null) => {
    const res = await api.post("/auth/login", {
      email,
      password,
      ...(totpCode ? { totp_code: totpCode } : {}),
    });
    if (res.data?.access_token) setToken(res.data.access_token);
    setUser(res.data);
    return res.data;
  };

  const register = async (email, password, name, role = "user") => {
    const res = await api.post("/auth/register", { email, password, name, role });
    if (res.data?.access_token) setToken(res.data.access_token);
    setUser(res.data);
    return res.data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setToken(null);
    setUser(false);
  };

  const refreshMe = async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      return res.data;
    } catch {
      setToken(null);
      setUser(false);
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshMe, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};

/** Pass axios error (`err`) or FastAPI `detail` from `err.response?.data?.detail`. */
export const formatApiError = (detailOrErr, maybeErr) => {
  let err = maybeErr;
  let detail = detailOrErr;
  if (detailOrErr?.isAxiosError) {
    err = detailOrErr;
    detail = err.response?.data?.detail;
  }

  if (detail != null) {
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      return detail
        .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
        .filter(Boolean)
        .join(" ");
    }
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
  }

  if (err?.response?.status === 404) {
    return `API not found at ${API_BASE}. Check REACT_APP_BACKEND_URL in frontend/.env.local and restart npm start.`;
  }
  if (!err?.response) {
    if (err?.code === "ERR_NETWORK") {
      return `Cannot reach API at ${API_BASE}. Start the backend (uvicorn) and confirm curl ${API_BASE}/health works.`;
    }
    return err?.message || "Something went wrong. Please try again.";
  }
  return "Something went wrong. Please try again.";
};
